export type PeerType = 'policy' | 'source'

export interface PeerRef {
  type: PeerType
  id: string
}

export type PeerReturnDirection = 'policy_to_source' | 'source_to_policy'

export interface PeerReturnContext {
  origin: PeerRef
  label: string
  destination: PeerRef
  returnTo: string
  scrollY: number
  state?: Record<string, unknown>
}

export interface PeerReturnScroll {
  returnTo: string
  scrollY: number
}

export interface PeerReturnState {
  returnTo: string
  state: Record<string, unknown>
}

export const PEER_RETURN_CTX_KEY = 'peer-return:ctx'
export const PEER_RETURN_SCROLL_KEY = 'peer-return:scroll'
export const PEER_RETURN_STATE_KEY = 'peer-return:state'
export const PEER_RETURN_SCROLL_ROOT = '[data-scroll-root]'
export const PEER_RETURN_TRACK_EVENT = 'peer_return_click'

const POLICY_PATH = /^\/content-library\/policies\/([^/]+)/
const SOURCE_PATH = /^\/content-library\/reference-documents\/([^/]+)/

export const peerFromPath = (path: string): PeerRef | null => {
  const policy = POLICY_PATH.exec(path)
  if (policy?.[1] && policy[1] !== 'new')
    return { type: 'policy', id: policy[1] }

  const source = SOURCE_PATH.exec(path)
  if (source?.[1]) return { type: 'source', id: source[1] }

  return null
}

export const peerEquals = (a: PeerRef | null, b: PeerRef | null): boolean =>
  Boolean(a && b && a.type === b.type && a.id === b.id)

export const peerReturnDirection = (origin: PeerRef): PeerReturnDirection =>
  origin.type === 'policy' ? 'policy_to_source' : 'source_to_policy'

const readJSON = <T>(key: string): T | null => {
  if (import.meta.server) return null

  try {
    const raw = sessionStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

const writeJSON = (key: string, value: unknown): void => {
  if (import.meta.server) return

  try {
    if (value == null) sessionStorage.removeItem(key)
    else sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // ponytail: sessionStorage unavailable (private mode/quota) — affordance just won't persist
  }
}

export const readPeerReturn = (): PeerReturnContext | null =>
  readJSON<PeerReturnContext>(PEER_RETURN_CTX_KEY)

export const writePeerReturn = (context: PeerReturnContext): void =>
  writeJSON(PEER_RETURN_CTX_KEY, context)

export const clearPeerReturn = (): void => writeJSON(PEER_RETURN_CTX_KEY, null)

export const readPeerReturnScroll = (): PeerReturnScroll | null =>
  readJSON<PeerReturnScroll>(PEER_RETURN_SCROLL_KEY)

export const writePeerReturnScroll = (scroll: PeerReturnScroll): void =>
  writeJSON(PEER_RETURN_SCROLL_KEY, scroll)

export const clearPeerReturnScroll = (): void =>
  writeJSON(PEER_RETURN_SCROLL_KEY, null)

export const readPeerReturnState = (): PeerReturnState | null =>
  readJSON<PeerReturnState>(PEER_RETURN_STATE_KEY)

export const writePeerReturnState = (payload: PeerReturnState): void =>
  writeJSON(PEER_RETURN_STATE_KEY, payload)

export const clearPeerReturnState = (): void =>
  writeJSON(PEER_RETURN_STATE_KEY, null)

export const getScrollRoot = (): HTMLElement | null =>
  import.meta.client
    ? document.querySelector<HTMLElement>(PEER_RETURN_SCROLL_ROOT)
    : null

/** Sets scrollTop now and again next frame, so it survives an async re-render. */
export const restoreScrollTop = (root: HTMLElement, y: number): void => {
  root.scrollTop = y
  requestAnimationFrame(() => {
    root.scrollTop = y
  })
}

type PendoClient = {
  track?: (event: string, metadata: Record<string, unknown>) => void
}

export const trackPeerReturn = (
  pendo: unknown,
  direction: PeerReturnDirection,
  originId: string,
): void => {
  const client = pendo as PendoClient | undefined
  client?.track?.(PEER_RETURN_TRACK_EVENT, {
    direction,
    origin_id: originId,
  })
}
